import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { HttpService } from '../_services/http.service';
import { UserService } from '../_services/user.service';
import { NGXLogger } from 'ngx-logger';
import { PasswordDataService } from '../_services/password-data.service';
import { CONFIGUREPORTALREQUEST } from '../_models/ConfigurePortal/configureportalrequest';
import { PasswordErrorMatcher, NewPasswordErrorMatcher } from '../_helpers/errorMatcher.interceptor';
import { CustomValidators } from '../_validators/password-validator';
import * as configList from '../../assets/config/config';

@Component({
  selector: 'app-forgot-password-reset',
  templateUrl: './forgot-password-reset.component.html',
  styleUrls: ['./forgot-password-reset.component.scss']
})
export class ForgotPasswordResetComponent implements OnInit {
  @ViewChild('newPassword') newPassword: any;
  @ViewChild('confirmPassword') confirmPassword: any;
  @ViewChild('qsModal') qsModal: any;
  @ViewChild('qsMsg') qsMsg: any;

  public consterror = configList.errorConst;
  public tooltipconstdata = configList.tooltipConst;
  public errorMsg: string;
  public showError = false;
  public showSuccess = false;
  public tokenInvalid = false;
  public disableSave = false;
  private token: string;
  passwordForm: FormGroup;
  securityQsForm: FormGroup;
  cnfPassFieldErrorMatcher = new PasswordErrorMatcher();
  newPassFieldErrorMatcher = new NewPasswordErrorMatcher();
  public hide = true;
  public reEnterhide = true;
  public pwdConfigRules: any = {};
  public errorMsgMethods: any;
  public finalReg: string;
  public tokenChecked = false;
  public secQuestionsVerified = false;
  public securityQSitems: any[] = [];
  public noOfQuestions = 0;
  public showSecQuesError = false;
  public secQuesErrorMsg: string;
  public disableQuestions = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private httpService: HttpService,
    private userDet: UserService,
    private logger: NGXLogger,
    private passwordData: PasswordDataService
  ) {
    this.errorMsgMethods = this.userDet;
  }

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    this.passwordData.getDataFromJson();
    this.passwordForm = this.formBuilder.group({
      newPassword: [''],
      confirmPassword: ['', [Validators.required]]
    }, { validator: [this.checkPassChanges] });
    this.securityQsForm = this.formBuilder.group({
      securityQuestions: this.formBuilder.array([])
    });
    this.onChanges();
    this.getPwdRules();
    this.pwdConfigRules = this.passwordData.passwordRulesData;
    this.validateToken();
  }

  get f() { return this.passwordForm.controls; }

  get secFormData() { return <FormArray>this.securityQsForm.get('securityQuestions'); }

  onChanges(): void {
    this.passwordForm.get('newPassword').valueChanges.subscribe(val => {
      this.passwordForm.get('newPassword').markAsTouched();
    });
    this.passwordForm.get('confirmPassword').valueChanges.subscribe(val => {
      this.passwordForm.get('confirmPassword').markAsTouched();
    });
  }

  getCnfPassError() {
    return this.passwordForm.get('confirmPassword').hasError('required') ? this.consterror.fieldError :
      this.passwordForm.hasError('passwordError') ? this.consterror.confirmPasswordError : '';
  }

  checkPassChanges(form: FormGroup) {
    return form.get('newPassword').value !== form.get('confirmPassword').value
      ? { passwordError: true } : null;
  }

  getSecurityAnswerError(i) {
    const control = this.securityQsForm.get('securityQuestions').get(String(i));
    if (!control) { return ''; }
    return control.get('answer_' + i).hasError('required') ? this.consterror.fieldError :
      control.get('answer_' + i).hasError('spaceError') ? this.consterror.onlySpaces : '';
  }

  checkSpaces(control: FormControl) {
    const condition = control.value !== null &&
      control.value.toString().trim() === '' &&
      control.value.toString().length > 0;
    return condition ? { spaceError: true } : null;
  }

  getSecurityQSFields(i) {
    return this.formBuilder.group({
      ['question_' + i]: [{ value: 0, disabled: true }],
      ['answer_' + i]: ['', [Validators.required, this.checkSpaces]]
    });
  }

  validateToken() {
    if (!this.token) {
      this.tokenInvalid = true;
      this.errorMsg = configList.errorConst.SystemError;
      this.tokenChecked = true;
      return;
    }

    const configurePortalRequest = new CONFIGUREPORTALREQUEST();
    configurePortalRequest.screenId = configList.pageConst.pagePublicFacing;
    configurePortalRequest.updatedBy = 'anonymous';
    configurePortalRequest.portletId = configList.pageConst.NAPortletId;
    configurePortalRequest.activityCode = configList.activityCode.validateForgotPasswordToken;

    const ruleEngineData: any = {};
    ruleEngineData.authToken = this.token;
    ruleEngineData.userLoginId = '';
    configurePortalRequest.ruleEngineData = ruleEngineData;

    const finalData: any = {};
    finalData.messageContent = this.userDet.encryptData(configurePortalRequest);

    this.httpService.secureUserLoginTrack(finalData).subscribe(data => {
      try {
        data = this.userDet.decryptData(data.messageContent);
        const code = data && data.ruleEngineResp && data.ruleEngineResp.responseCode;
        if (code !== 'RP121') {
          this.tokenInvalid = true;
          this.errorMsg = configList.errorConst.invalidResetToken;
          this.tokenChecked = true;
        } else {
          this.loadSecurityQuestions();
        }
      } catch (e) {
        this.logger.debug('Error processing token validation response: ' + e);
        this.tokenInvalid = true;
        this.errorMsg = configList.errorConst.SystemError;
        this.tokenChecked = true;
      }
    }, _error => {
      this.logger.debug('Error while validating token');
      this.tokenChecked = true;
    });
  }

  loadSecurityQuestions() {
    const configurePortalRequest = new CONFIGUREPORTALREQUEST();
    configurePortalRequest.screenId = configList.pageConst.pagePublicFacing;
    configurePortalRequest.updatedBy = 'anonymous';
    configurePortalRequest.portletId = configList.pageConst.NAPortletId;
    configurePortalRequest.activityCode = configList.activityCode.getForgotPasswordSecurityQuestions;

    const ruleEngineData: any = {};
    ruleEngineData.authToken = this.token;
    ruleEngineData.userLoginId = '';
    configurePortalRequest.ruleEngineData = ruleEngineData;

    const finalData: any = {};
    finalData.messageContent = this.userDet.encryptData(configurePortalRequest);

    this.httpService.secureUserLoginTrack(finalData).subscribe(data => {
      try {
        data = this.userDet.decryptData(data.messageContent);
        const questions = data && data.ruleEngineResp && data.ruleEngineResp.securityQuestions;
        if (questions && questions.length > 0) {
          this.securityQSitems = questions;
          this.noOfQuestions = questions.length;
          const arrayControl = <FormArray>this.securityQsForm.controls['securityQuestions'];
          for (let i = 0; i < questions.length; i++) {
            arrayControl.push(this.getSecurityQSFields(i));
            this.securityQsForm.get('securityQuestions')?.get(String(i))?.get('question_' + i)?.setValue(questions[i].questionId);
          }
        }
      } catch (e) {
        this.logger.debug('Error processing security questions response: ' + e);
      }
      if (this.securityQSitems.length === 0) {
        this.tokenInvalid = true;
        this.errorMsg = configList.errorConst.SystemError;
      }
      this.tokenChecked = true;
    }, _error => {
      this.logger.debug('Error while loading security questions');
      this.tokenInvalid = true;
      this.errorMsg = configList.errorConst.SystemError;
      this.tokenChecked = true;
    });
  }

  submitSecurityQuestions() {
    this.showSecQuesError = false;
    const answers = [];
    for (let i = 0; i < this.noOfQuestions; i++) {
      answers.push({
        questionId: this.securityQSitems[i].questionId,
        answer: this.securityQsForm.get('securityQuestions').get(String(i)).get('answer_' + i).value
      });
    }

    const configurePortalRequest = new CONFIGUREPORTALREQUEST();
    configurePortalRequest.screenId = configList.pageConst.pagePublicFacing;
    configurePortalRequest.updatedBy = 'anonymous';
    configurePortalRequest.portletId = configList.pageConst.NAPortletId;
    configurePortalRequest.activityCode = configList.activityCode.validateForgotPasswordSecurityQuestions;

    const ruleEngineData: any = {};
    ruleEngineData.authToken = this.token;
    ruleEngineData.userLoginId = '';
    ruleEngineData.securityAnswers = answers;
    configurePortalRequest.ruleEngineData = ruleEngineData;

    const finalData: any = {};
    finalData.messageContent = this.userDet.encryptData(configurePortalRequest);

    this.httpService.secureUserLoginTrack(finalData).subscribe(data => {
      try {
        data = this.userDet.decryptData(data.messageContent);
        const code = data && data.ruleEngineResp && data.ruleEngineResp.responseCode;
        if (code === 'RP122') {
          this.secQuestionsVerified = true;
        } else {
          this.secQuesErrorMsg = configList.errorConst.invalidSecurityQuestions;
          this.showSecQuesError = true;
          if (this.qsModal !== undefined) {
            this.qsModal.nativeElement.style.display = 'block';
          }
          if (this.qsMsg !== undefined) {
            this.qsMsg.nativeElement.focus();
          }
        }
      } catch (e) {
        this.logger.debug('Error processing security questions validation response: ' + e);
        this.secQuesErrorMsg = configList.errorConst.SystemError;
        this.showSecQuesError = true;
        if (this.qsModal !== undefined) {
          this.qsModal.nativeElement.style.display = 'block';
        }
      }
    }, _error => {
      this.logger.debug('Error while validating security questions');
      this.secQuesErrorMsg = configList.errorConst.SystemError;
      this.showSecQuesError = true;
      if (this.qsModal !== undefined) {
        this.qsModal.nativeElement.style.display = 'block';
      }
    });
  }

  closeSecQuesModal() {
    this.showSecQuesError = false;
    if (this.qsModal !== undefined) {
      this.qsModal.nativeElement.style.display = 'none';
    }
  }

  getPwdRules() {
    this.httpService.getJsonContent(configList.pwdconfigjson).subscribe(
      (data: any) => {
        this.pwdConfigRules = data;
        const validatorsArray = [];
        validatorsArray.push(Validators.required);
        const listRegShouldContain = [];
        this.finalReg = '';

        if (this.pwdConfigRules.passwordMinLength || this.pwdConfigRules.passwordMaxLength) {
          validatorsArray.push(CustomValidators.patternValidator(
            this.userDet.getMinMaxRegex(configList.pageConst.passwordMinMaxRegex, '1'),
            { pswdMinMaxError: true }));
        }
        if (this.pwdConfigRules.passwordShouldContainUpperCase) {
          listRegShouldContain.push(this.userDet.getRegexValidate(
            configList.pageConst.passwordUpperCaseRegex, this.pwdConfigRules.passwordUpperCaseLength));
        }
        if (this.pwdConfigRules.passwordShouldContainLowerCase) {
          listRegShouldContain.push(this.userDet.getRegexValidate(
            configList.pageConst.passwordLowerCaseRegex, this.pwdConfigRules.passwordLowerCaseLength));
        }
        if (this.pwdConfigRules.passwordShouldContainNumber) {
          listRegShouldContain.push(this.userDet.getRegexValidate(
            configList.pageConst.passwordNumberRegex, this.pwdConfigRules.passwordNumberLength));
        }
        if (this.pwdConfigRules.passwordShouldContainSpecialChar) {
          listRegShouldContain.push(this.userDet.getRegexValidate(
            configList.pageConst.passwordSpecialCharRegex, this.pwdConfigRules.passwordSpecialCharLength));
        }

        if (listRegShouldContain) {
          const lengthOfListRegArr = listRegShouldContain.length;
          let finalRegexforContains: RegExp;
          if (lengthOfListRegArr <= this.pwdConfigRules.passwordContainCategories) {
            this.finalReg = '(';
            this.finalReg += listRegShouldContain.join('');
            this.finalReg += ').*';
            finalRegexforContains = new RegExp(this.finalReg);
          } else {
            const storeRegArray = [];
            this.finalReg = '(';
            this.combinationUtil(listRegShouldContain, lengthOfListRegArr,
              this.pwdConfigRules.passwordContainCategories, 0, storeRegArray, 0);
            this.finalReg = this.finalReg.slice(0, -1);
            this.finalReg += ').*';
            finalRegexforContains = new RegExp(this.finalReg);
          }
          validatorsArray.push(CustomValidators.patternValidator(finalRegexforContains, { pswdMustContainError: true }));
        }

        if (this.pwdConfigRules.consecutiveCharNotAllowed) {
          validatorsArray.push(CustomValidators.containPatternValator(
            configList.pageConst.passwordConsecutiveRegex, { pswdContainsError: true }));
        }
        if (this.pwdConfigRules.capNumberSplCharNotAllowed) {
          validatorsArray.push(CustomValidators.containPatternValator(
            configList.pageConst.passwordCapNoSpl, { pswdContainsError: true }));
        }
        if (this.pwdConfigRules.splCharNumberCapNotAllowed) {
          validatorsArray.push(CustomValidators.containPatternValator(
            configList.pageConst.passwordSplNoCap, { pswdContainsError: true }));
        }
        if (this.pwdConfigRules.userIdNotAllowed || this.pwdConfigRules.phoneNumberNotAllowed) {
          validatorsArray.push(this.checkUserDetails.bind(this));
        }
        validatorsArray.push(CustomValidators.containPatternValator(
          configList.pageConst.passwordSplNoAccept, { pswdMustContainError: true }));
        validatorsArray.push(CustomValidators.containPatternValator(
          configList.pageConst.passwordNoSpaces, { pswdNoSpaceError: true }));

        this.passwordForm.get('newPassword').setValidators(validatorsArray);
        this.passwordForm.get('newPassword').updateValueAndValidity();
      },
      error => {
        this.logger.debug('Error while getting password config rules');
      }
    );
  }

  pswdContainError() {
    let containErrorMsg: string = '';
    if (this.pwdConfigRules) {
      containErrorMsg = '&nbsp;' + configList.errorConst.newPasswordContainError;
      containErrorMsg += "<ul class='mb-0'>";
      if (this.pwdConfigRules.userIdNotAllowed) {
        containErrorMsg += '<li>' + configList.errorConst.newPasswordUsernameError + '</li>';
      }
      if (this.pwdConfigRules.phoneNumberNotAllowed) {
        containErrorMsg += '<li>' + configList.errorConst.newPasswordPhoneError + '</li>';
      }
      if (this.pwdConfigRules.consecutiveCharNotAllowed) {
        containErrorMsg += '<li>' + configList.errorConst.newPasswordConseRegex + '</li>';
      }
      if (this.pwdConfigRules.capNumberSplCharNotAllowed) {
        containErrorMsg += '<li>' + configList.errorConst.newPasswordCapNumSplReg + '</li>';
      }
      if (this.pwdConfigRules.splCharNumberCapNotAllowed) {
        containErrorMsg += '<li>' + configList.errorConst.newPasswordSplNumCapReg + '</li>';
      }
      containErrorMsg += '</ul>';
      if (!(this.pwdConfigRules.userIdNotAllowed || this.pwdConfigRules.phoneNumberNotAllowed ||
        this.pwdConfigRules.consecutiveCharNotAllowed || this.pwdConfigRules.capNumberSplCharNotAllowed ||
        this.pwdConfigRules.splCharNumberCapNotAllowed)) {
        containErrorMsg = null;
      }
    }
    return containErrorMsg;
  }

  pswdMustContainError() {
    let containErrorMsg: string = '';
    if (this.pwdConfigRules) {
      containErrorMsg = '&nbsp;' + this.userDet.pswdMustContainErrorMsg();
      containErrorMsg += "<ul class='mb-0'>";
      if (this.pwdConfigRules.passwordShouldContainUpperCase) {
        containErrorMsg += '<li>' + this.userDet.pswdUpperCaseErrorMessage() + '</li>';
      }
      if (this.pwdConfigRules.passwordShouldContainLowerCase) {
        containErrorMsg += '<li>' + this.userDet.pswdLowerCaseErrorMessage() + '</li>';
      }
      if (this.pwdConfigRules.passwordShouldContainNumber) {
        containErrorMsg += '<li>' + this.userDet.pswdNumberErrorMessage() + '</li>';
      }
      if (this.pwdConfigRules.passwordShouldContainSpecialChar) {
        containErrorMsg += '<li>' + this.userDet.pswdSpecialCharErrorMessage() + '</li>';
      }
      containErrorMsg += '</ul>';
      if (!(this.pwdConfigRules.passwordShouldContainUpperCase || this.pwdConfigRules.passwordShouldContainLowerCase ||
        this.pwdConfigRules.passwordShouldContainNumber || this.pwdConfigRules.passwordShouldContainSpecialChar)) {
        containErrorMsg = null;
      }
    }
    return containErrorMsg;
  }

  checkUserDetails(control: FormControl) {
    return null;
  }

  combinationUtil(arr, n, r, index, data, i) {
    if (index === r) {
      for (let j = 0; j < r; j++) {
        this.finalReg += data[j];
      }
      this.finalReg += '|';
      return;
    }
    if (i >= n) { return; }
    data[index] = arr[i];
    this.combinationUtil(arr, n, r, index + 1, data, i + 1);
    this.combinationUtil(arr, n, r, index, data, i + 1);
  }

  submitNewPassword() {
    this.showError = false;
    this.showSuccess = false;
    this.tokenInvalid = false;

    if (this.passwordForm.invalid) {
      return;
    }

    const configurePortalRequest = new CONFIGUREPORTALREQUEST();
    configurePortalRequest.screenId = configList.pageConst.pagePublicFacing;
    configurePortalRequest.updatedBy = 'anonymous';
    configurePortalRequest.portletId = configList.pageConst.NAPortletId;
    configurePortalRequest.activityCode = configList.activityCode.forgotPasswordReset;

    const ruleEngineData: any = {};
    ruleEngineData.authToken = this.token;
    ruleEngineData.newPassword = this.f.newPassword.value;
    ruleEngineData.userLoginId = '';
    configurePortalRequest.ruleEngineData = ruleEngineData;

    const finalData: any = {};
    finalData.messageContent = this.userDet.encryptData(configurePortalRequest);

    this.httpService.secureUserLoginTrack(finalData).subscribe(data => {
      try {
        data = this.userDet.decryptData(data.messageContent);
        const code = data && data.ruleEngineResp && data.ruleEngineResp.responseCode;
        if (code === 'RP002') {
          this.showSuccess = true;
          this.errorMsg = (data.ruleEngineResp && data.ruleEngineResp.responseDesc) || 'Password changed successfully';
          setTimeout(() => this.router.navigate(['/publicFacing']), 3000);
        } else if (code === 'RP117' || code === 'RP118') {
          this.tokenInvalid = true;
          this.errorMsg = (data.ruleEngineResp && data.ruleEngineResp.responseDesc) || configList.errorConst.invalidResetToken;
        } else if (code === 'RP104' || code === 'RP106') {
          this.showError = true;
          this.errorMsg = (data.ruleEngineResp && data.ruleEngineResp.responseDesc) || configList.errorConst.SystemError;
        } else {
          this.showError = true;
          this.errorMsg = configList.errorConst.SystemError;
        }
      } catch (e) {
        this.logger.debug('Error processing password reset response: ' + e);
        this.showError = true;
        this.errorMsg = configList.errorConst.SystemError;
      }
    },
    error => {
      this.logger.debug('Error while resetting password via token');
      this.showError = true;
      this.errorMsg = configList.errorConst.SystemError;
    });
  }

  cancelReset(): void {
    this.router.navigate(['/publicFacing']);
  }
}
